import { RESULTS_MORE_BUFFER_SIZE } from '~/components/AssetModalV2/Content/Panel/Transcript/constants';
import {
  type FetchedSegment,
  type TranscriptSegmentData,
} from '~/components/AssetModalV2/Content/Panel/Transcript/types';
import { type FetchSearchResultsForRangeType } from '~/providers/TranscriptSearchResultsProvider';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type TranscriptSearchedItem } from '~/swr-hooks/clips/transcript/search/types';
import { type ClipTranscriptCache, type TimeRange } from '~/swr-hooks/clips/transcript/types';

export const isInRange = (numberToCheck: number, range: [number, number]) =>
  range[0] <= numberToCheck && numberToCheck <= range[1];

export const isTimeInSearchResults = (results: TranscriptSearchedItem[], startTimeMs: number, endTimeMs: number) =>
  results.some(
    (result) =>
      result.type === 'result' &&
      (isInRange(startTimeMs, [result.startTimeMs, result.endTimeMs]) ||
        isInRange(endTimeMs, [result.startTimeMs, result.endTimeMs])),
  );

type PrefetchSearchResultsParams = {
  newIndex: number;
  searchResults: TranscriptSearchedItem[];
  fetchSearchResultsForRange: FetchSearchResultsForRangeType;
};

export const prefetchNextSearchResults = async ({
  newIndex,
  searchResults,
  fetchSearchResultsForRange,
}: PrefetchSearchResultsParams) => {
  const nextIndexToCheck = Math.min(newIndex + RESULTS_MORE_BUFFER_SIZE, searchResults.length - 1);
  for (let i = newIndex; i <= nextIndexToCheck; i++) {
    const result = searchResults[i];
    if (result?.type === 'empty') {
      const startTime = result.startTimeMs;
      const endTime = startTime + MAX_TRANSCRIPT_RANGE_IN_MS;
      await fetchSearchResultsForRange({ startTimeMs: startTime, endTimeMs: endTime });
      break;
    }
  }
};

export const prefetchPreviousSearchResults = async ({
  fetchSearchResultsForRange,
  searchResults,
  newIndex,
}: PrefetchSearchResultsParams) => {
  const prevIndexToCheck = newIndex === 0 ? searchResults.length - 1 : newIndex;
  const lastIndexToCheck = Math.max(0, prevIndexToCheck - RESULTS_MORE_BUFFER_SIZE);

  for (let i = prevIndexToCheck; i >= lastIndexToCheck; i--) {
    const result = searchResults[i];
    if (result?.type === 'empty') {
      const endTime = result.endTimeMs;
      const startTime = Math.max(0, endTime - MAX_TRANSCRIPT_RANGE_IN_MS);
      await fetchSearchResultsForRange({ startTimeMs: startTime, endTimeMs: endTime });
      break;
    }
  }
};

type ConvertTranscriptRangesToFlatSegmentsParams = {
  transcript: ClipTranscriptCache['data'];
  hasMore: boolean;
  durationMs: number;
};

/**
 * This method flattens transcript ranges to segments items that can be displayed in the list
 * Each segment has a type, fetched or empty:
 * - empty - this is a gap between fetched ranges, showed as a loading placeholder
 * - fetched - this is a segment with transcript data
 */
export const convertTranscriptRangesToFlatSegments = ({
  transcript,
  durationMs,
  hasMore,
}: ConvertTranscriptRangesToFlatSegmentsParams): TranscriptSegmentData[] => {
  const segments: TranscriptSegmentData[] = [];

  // Create segments array from fetched ranges
  // if all data for a transcript is fetched, there will be only one fetched range
  for (let i = 0; i < transcript.length; i++) {
    const fetchedRange = transcript[i];
    // if the first element is not at 0, add an empty segment at the beginning
    if (i === 0 && fetchedRange.startTimeMs !== 0) {
      segments.push({ type: 'empty', startTimeMs: 0, endTimeMs: fetchedRange.startTimeMs, position: 'top' });
    }
    const rangeSegments: FetchedSegment[] = fetchedRange.data.map((segment) => ({ type: 'fetched', ...segment }));
    segments.push(...rangeSegments);
    if (i < transcript.length - 1) {
      // if there are more items in transcript array, that means a hole in fetched ranges
      // we need to add empty item between fetched ranges
      const nextRange = transcript[i + 1];

      segments.push({
        type: 'empty',
        startTimeMs: fetchedRange.endTimeMs,
        endTimeMs: nextRange.startTimeMs,
        position: 'top',
      });
    } else if (hasMore && fetchedRange.endTimeMs < durationMs) {
      // if there are more items to fetch, add an empty segment at the end
      segments.push({ type: 'empty', startTimeMs: fetchedRange.endTimeMs, endTimeMs: durationMs, position: 'top' });
    }
  }

  return segments;
};

type FindBestResultsRangeToFetchParams = {
  timeRange: TimeRange;
  previousEndTime: number | null;
  nextStartTime: number | null;
  maxRangeSize: number;
};

export const findBestResultsRangeToFetch = ({
  timeRange,
  previousEndTime,
  nextStartTime,
  maxRangeSize,
}: FindBestResultsRangeToFetchParams) => {
  let startTimeMs = timeRange.startTimeMs;
  let endTimeMs = timeRange.endTimeMs;

  const startTimeDiff = previousEndTime ? startTimeMs - previousEndTime : Infinity;
  const endTimeDiff = nextStartTime ? nextStartTime - endTimeMs : Infinity;

  const duration = timeRange.endTimeMs - timeRange.startTimeMs;
  const availableRange = (maxRangeSize - duration) / 2;

  if (previousEndTime && startTimeDiff < endTimeDiff) {
    startTimeMs = startTimeDiff > availableRange ? timeRange.startTimeMs - availableRange : previousEndTime;
    endTimeMs = startTimeMs + maxRangeSize;
  } else if (nextStartTime && endTimeDiff < startTimeDiff) {
    endTimeMs = endTimeDiff > availableRange ? timeRange.endTimeMs + availableRange : nextStartTime;
    startTimeMs = Math.max(0, endTimeMs - maxRangeSize);
  } else {
    startTimeMs = Math.max(0, timeRange.startTimeMs - availableRange);
    endTimeMs = startTimeMs + maxRangeSize;
  }

  return { startTimeMs, endTimeMs };
};
